import { Mic, MicOff, Volume2, VolumeX, Settings2, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DeviceSelect = ({ label, value, list, onChange, testid, fallback }) => (
  <div className="space-y-1.5">
    <p className="text-xs font-medium text-slate-300">{label}</p>
    <Select value={value || "default"} onValueChange={(v) => onChange(v === "default" ? "" : v)}>
      <SelectTrigger className="h-9 bg-slate-900 border-slate-700" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="default">Par défaut du système</SelectItem>
        {list.filter((d) => d.deviceId && d.deviceId !== "default").map((d, i) => (
          <SelectItem key={d.deviceId} value={d.deviceId}>{d.label || `${fallback} ${i + 1}`}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

export const AudioControls = ({ voice }) => {
  const toggleCall = async () => {
    try { await voice.toggle(); } catch { toast.error("Micro indisponible : autorisez le micro dans le navigateur."); }
  };
  const changeMic = async (id) => {
    try { await voice.chooseMic(id); } catch { toast.error("Impossible d'utiliser ce micro"); }
  };

  return (
    <div className="flex items-center gap-0.5" data-testid="audio-controls">
      <Button variant={voice.active ? "secondary" : "ghost"} size="sm" onClick={toggleCall} data-testid="remote-voice-toggle-btn" className={voice.active ? "text-emerald-400" : ""}>
        <PhoneCall className="h-4 w-4 mr-1.5" /> {voice.active ? "Audio actif" : "Son"}
      </Button>
      {voice.active && <>
        <Button variant="ghost" size="icon" className={`h-8 w-8 ${voice.micMuted ? "text-red-400" : ""}`} onClick={voice.toggleMic} title={voice.micMuted ? "Réactiver mon micro" : "Couper mon micro"} data-testid="audio-mic-mute-btn">
          {voice.micMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
        </Button>
        <Button variant="ghost" size="icon" className={`h-8 w-8 ${voice.speakerMuted ? "text-red-400" : ""}`} onClick={voice.toggleSpeaker} title={voice.speakerMuted ? "Réactiver le son" : "Couper le son"} data-testid="audio-speaker-mute-btn">
          {voice.speakerMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        </Button>
      </>}
      <Popover onOpenChange={(o) => o && voice.loadDevices()}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8" title="Réglages audio" data-testid="audio-settings-btn"><Settings2 className="h-4 w-4" /></Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 bg-[#0B1120] border-slate-700 text-slate-200 space-y-4" data-testid="audio-settings-panel">
          <p className="font-semibold text-sm">Réglages audio</p>
          <DeviceSelect label="Micro" value={voice.micId} list={voice.devices.inputs} onChange={changeMic} testid="audio-mic-select" fallback="Micro" />
          {voice.canPickSpeaker
            ? <DeviceSelect label="Haut-parleurs" value={voice.speakerId} list={voice.devices.outputs} onChange={voice.chooseSpeaker} testid="audio-speaker-select" fallback="Sortie" />
            : <p className="text-xs text-slate-400" data-testid="audio-speaker-unsupported">Le choix des haut-parleurs n'est pas proposé par ce navigateur : le son sort sur la sortie par défaut.</p>}
          <p className="text-[11px] text-slate-500">Les noms des appareils s'affichent après la première autorisation du micro.</p>
        </PopoverContent>
      </Popover>
    </div>
  );
};
